import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import logoImg from "../assets/rhcwf-logo.jpeg";

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const userName = localStorage.getItem("userName") || "Admin User";
  const role = localStorage.getItem("role");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userId");
    localStorage.removeItem("userName");
    localStorage.removeItem("LOGGED_USER");
    localStorage.removeItem("userRole");
    navigate("/login", { replace: true });
  };

  const getDashboardPath = () => {
    switch(role) {
      case "ADMIN": return "/dashboard";
      case "GP_COORDINATOR": return "/gp-dashboard";
      case "STATE_COORDINATOR": return "/state-dashboard";
      case "DISTRICT_COORDINATOR": return "/district-dashboard";
      case "BLOCK_COORDINATOR": return "/block-dashboard";
      default: return "/profile";
    }
  };

  // Close sidebar when clicking a link on mobile
  const handleMobileNavClick = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Menu Button - Always on top */}
      {isMobile && (
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{
            position: 'fixed',
            top: '15px',
            left: '15px',
            zIndex: 10001,
            width: '44px',
            height: '44px',
            border: 'none',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #4361ee 0%, #764ba2 100%)',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}
        >
          {mobileOpen ? '✕' : '☰'}
        </button>
      )}

      {/* Overlay */}
      {isMobile && mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9998,
            animation: 'fadeIn 0.3s ease',
          }}
        />
      )}

      {/* Sidebar Container */}
      <div 
        className={`sidebar-container ${isCollapsed && !isMobile ? 'collapsed' : ''}`}
        style={{
          position: isMobile ? 'fixed' : 'sticky',
          top: 0,
          left: isMobile ? (mobileOpen ? '0' : '-280px') : 'auto',
          height: '100vh',
          zIndex: 9999,
          transition: 'left 0.3s ease',
        }}
      >
        <aside className="sidebar">
          {/* Toggle Button - Desktop only */}
          {!isMobile && (
            <button className="sidebar-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? '→' : '←'}
            </button>
          )}

          {/* Logo Section */}
          <div className="logo-section">
            {(!isCollapsed || isMobile) ? (
              <div className="logo-wrapper">
                <div className="rotating-logo">
                  <svg viewBox="0 0 200 200">
                    <defs>
                      <path id="circlePath" d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0" />
                    </defs>
                    <text className="rotating-text">
                      <textPath href="#circlePath" startOffset="0%">
                        RECONNECTING HEALTH CARE • AND WELFARE FOUNDATION--  2026  --
                      </textPath>
                    </text>
                  </svg>
                  <div className="center-logo">
                    <img src={logoImg} alt="RHCWF Logo" className="main-logo-img" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="collapsed-logo">🌿</div>
            )}
          </div>

          {/* User Profile */}
          {(!isCollapsed || isMobile) && (
            <div className="user-profile">
              <div className="user-avatar">
                <span>{userName.charAt(0).toUpperCase()}</span>
              </div>
              <div className="user-details">
                <div className="user-name">{userName}</div>
                <div className="user-role">{role?.replace('_', ' ') || 'User'}</div>
              </div>
            </div>
          )}

          {/* Navigation Menu */}
          <nav className="nav-menu">
            {(!isCollapsed || isMobile) && <div className="nav-section">MAIN MENU</div>}
            
            <NavItem 
              to={getDashboardPath()} 
              label="Dashboard" 
              icon="🏠"
              isActive={location.pathname === getDashboardPath()}
              collapsed={isCollapsed && !isMobile}
              onClick={handleMobileNavClick}
            />
            
            <NavItem 
              to="/teams" 
              label="Teams" 
              icon="👥"
              isActive={location.pathname === "/teams"}
              collapsed={isCollapsed && !isMobile}
              onClick={handleMobileNavClick}
            />
            
            {role === "ADMIN" && (
              <NavItem 
                to="/CreateUser" 
                label="Create User" 
                icon="👤"
                isActive={location.pathname === "/CreateUser"}
                collapsed={isCollapsed && !isMobile}
                onClick={handleMobileNavClick}
              />
            )}

            {(role === "ADMIN" || role === "GP_COORDINATOR") && (
              <>
                {(!isCollapsed || isMobile) && <div className="nav-section">BENEFICIARIES</div>}
                <NavItem 
                  to="/beneficiaries" 
                  label="Beneficiaries" 
                  icon="👥"
                  isActive={location.pathname === "/beneficiaries"}
                  collapsed={isCollapsed && !isMobile}
                  onClick={handleMobileNavClick}
                />
              </>
            )}

            {(!isCollapsed || isMobile) && <div className="nav-section">ACCOUNT</div>}
            
            <NavItem 
              to="/profile" 
              label="Settings" 
              icon="⚙️"
              isActive={location.pathname === "/profile"}
              collapsed={isCollapsed && !isMobile}
              onClick={handleMobileNavClick}
            />
            
            {/* Logout Button */}
            <div className="nav-item logout-item" onClick={() => {
              handleLogout();
              handleMobileNavClick();
            }}>
              <span className="nav-icon">🚪</span>
              {(!isCollapsed || isMobile) && <span>Logout</span>}
            </div>
          </nav>
        </aside>
      </div>

      <style jsx>{`
        /* CSS Variables */
        :root {
          --primary: #4361ee;
          --primary-dark: #3b0a6b;
          --secondary: #764ba2;
          --accent: #fbbf24;
          --text-primary: #1e293b;
          --text-secondary: #64748b;
          --text-muted: #94a3b8;
          --border: #e2e8f0;
          --bg-hover: #f1f5f9;
          --bg-sidebar: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          --shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .sidebar {
          width: 280px;
          height: 100vh;
          background: var(--bg-sidebar);
          border-right: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          position: relative;
          transition: var(--transition);
          overflow-y: auto;
          overflow-x: hidden;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
        }

        .sidebar-container.collapsed .sidebar {
          width: 80px;
        }

        /* Custom Scrollbar */
        .sidebar::-webkit-scrollbar {
          width: 4px;
        }

        .sidebar::-webkit-scrollbar-track {
          background: var(--border);
        }

        .sidebar::-webkit-scrollbar-thumb {
          background: var(--text-muted);
          border-radius: 4px;
        }

        .sidebar::-webkit-scrollbar-thumb:hover {
          background: var(--text-secondary);
        }

        /* Toggle Button - Desktop */
        .sidebar-toggle {
          position: absolute;
          right: -12px;
          top: 80px;
          width: 24px;
          height: 24px;
          background: white;
          border: 1px solid var(--border);
          border-radius: 50%;
          color: var(--text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          z-index: 10;
          transition: var(--transition);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .sidebar-toggle:hover {
          transform: scale(1.1);
          background: var(--primary);
          color: white;
          border-color: var(--primary);
        }

        /* Logo Section */
        .logo-section {
          padding: 30px 20px;
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 180px;
          position: relative;
        }

        .logo-wrapper {
          position: relative;
          width: 160px;
          height: 160px;
        }

        .rotating-logo {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .rotating-logo svg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          animation: rotate 20s linear infinite;
        }

        .rotating-text {
          font-size: 10px;
          font-weight: 700;
          fill: white;
          letter-spacing: 2px;
        }

        .center-logo {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 120px;
          height: 120px;
          background: rgba(255, 255, 255, 0.98);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 20px rgba(0,0,0,0.2);
          border: 3px solid var(--accent);
          z-index: 2;
        }

        .main-logo-img {
          width: 74px;
          height: 74px;
          border-radius: 50%;
          object-fit: cover;
        }

        .collapsed-logo {
          font-size: 40px;
          animation: pulse 2s ease-in-out infinite;
        }

        /* User Profile */
        .user-profile {
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid var(--border);
          background: white;
        }

        .user-avatar {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 600;
          color: white;
          box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
        }

        .user-details {
          flex: 1;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .user-role {
          font-size: 10px;
          color: var(--text-secondary);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Navigation Menu */
        .nav-menu {
          flex: 1;
          padding: 20px 16px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .nav-section {
          font-size: 10px;
          font-weight: 700;
          color: var(--text-muted);
          margin-top: 16px;
          margin-bottom: 10px;
          margin-left: 12px;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }

        .nav-item {
          padding: 10px 14px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          color: var(--text-secondary);
          transition: var(--transition);
          cursor: pointer;
          position: relative;
        }

        .nav-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 0;
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          border-radius: 0 2px 2px 0;
          transition: var(--transition);
        }

        .nav-item.active::before {
          height: 20px;
        }

        .nav-item.active {
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);
        }

        .nav-item:not(.active):hover {
          background: var(--bg-hover);
          color: var(--text-primary);
          transform: translateX(4px);
        }

        .nav-icon {
          font-size: 18px;
          width: 24px;
          display: inline-block;
        }

        .logout-item {
          margin-top: auto;
          border-top: 1px solid var(--border);
          border-radius: 0;
          padding-top: 16px;
          margin-top: 16px;
        }

        .logout-item:hover {
          background: #fee2e2;
          color: #dc2626;
        }

        /* Animations */
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .sidebar {
            width: 280px;
            transform: translateX(0);
          }

          .logo-section {
            min-height: 140px;
            padding: 20px;
          }

          .logo-wrapper {
            width: 120px;
            height: 120px;
          }

          .center-logo {
            width: 85px;
            height: 85px;
          }

          .main-logo-img {
            width: 55px;
            height: 55px;
          }

          .nav-item {
            padding: 12px 14px;
            margin: 2px 0;
          }

          .nav-icon {
            font-size: 20px;
            width: 28px;
          }

          /* Ensure content below sidebar is clickable when sidebar is closed */
          body {
            overflow-x: hidden;
          }
        }
      `}</style>
    
    </>
  );
  
}

// Enhanced NavItem Component
function NavItem({ to, label, icon, isActive, collapsed, onClick }) {
  return (
    <Link
      to={to}
      className={`nav-item ${isActive ? 'active' : ''}`}
      onClick={onClick}
    >
      <span className="nav-icon">{icon}</span>
      {!collapsed && <span>{label}</span>}
    </Link>
  );
}